/* import __COLOCATED_TEMPLATE__ from './results-card.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';

/**
 * ResultsCard Component
 *
 * Displays race results in a configurable table with:
 * - Column visibility toggling (overall mode only)
 * - Sortable columns with click-to-sort
 * - Sticky columns for mobile scrolling
 * - Split time display (elapsed + split)
 * - Watchlist toggle for tracking athletes
 *
 * @param {Array} results - Array of result objects from results-calculator
 * @param {Object} classGroup - The classgroup object
 * @param {Array} splitColumns - Array of split column definitions from controller
 * @param {String} classGroupUuid - UUID for preference storage
 * @param {String} eventUuid - Event UUID for watchlist storage
 * @param {String} eventName - Event name for display
 * @param {String} viewMode - 'overall' (default) or 'element' for focused split view
 * @param {String} focusedElementName - Name of the focused element (element mode only)
 */
export default class ResultsCardComponent extends Component {
  @service preferences;
  @service watchlist;

  @tracked showColumnSelector = false;

  // Sort state - loaded from preferences
  @tracked sortField = 'pos';
  @tracked sortDirection = 'asc';
  @tracked splitSortMode = 'elapsed';

  constructor() {
    super(...arguments);

    // In element mode, default sort by the split column
    if (this.isElementMode && this.args.splitColumns?.length > 0) {
      this.sortField = `split_${this.args.splitColumns[0].uuid}`;
      this.sortDirection = 'asc';
      this.splitSortMode = 'elapsed';
    } else {
      // Load sort config from preferences
      const sortConfig = this.preferences.getSortConfig(this.args.classGroupUuid);
      this.sortField = sortConfig.sortField;
      this.sortDirection = sortConfig.sortDirection;
      this.splitSortMode = sortConfig.splitSortMode;
    }
  }

  /**
   * Whether we're in element view mode (focused on single split)
   */
  get isElementMode() {
    return this.args.viewMode === 'element';
  }

  get availableColumns() {
    return this.preferences.getAvailableColumns(this.args.splitColumns || []);
  }

  get visibleColumnIds() {
    // In element mode, show only pos, bib, name, and the single split
    if (this.isElementMode) {
      const splitColumns = this.args.splitColumns || [];
      const splitIds = splitColumns.map(s => `split_${s.uuid}`);
      return ['pos', 'bib', 'name', ...splitIds];
    }

    const config = this.preferences.getClassGroupConfig(this.args.classGroupUuid, this.args.splitColumns || []);
    const visible = [...config.visibleColumns];

    // Always include alwaysVisible columns (like pos)
    const alwaysVisibleIds = this.availableColumns
      .filter(col => col.alwaysVisible)
      .map(col => col.id);

    alwaysVisibleIds.forEach(id => {
      if (!visible.includes(id)) {
        visible.unshift(id); // Add at beginning
      }
    });

    return visible;
  }

  get visibleColumns() {
    const visibleIds = this.visibleColumnIds;
    return this.availableColumns
      .filter(col => visibleIds.includes(col.id))
      .map(col => {
        const isSorted = this.sortField === col.id;
        const isSplitColumn = col.id.startsWith('split_') || col.id === 'finish';

        let sortIndicator = '';
        if (isSorted) {
          const arrow = this.sortDirection === 'asc' ? '↑' : '↓';
          if (isSplitColumn) {
            const mode = this.splitSortMode === 'elapsed' ? 'E' : 'S';
            sortIndicator = `${arrow}${mode}`;
          } else {
            sortIndicator = arrow;
          }
        }

        return {
          ...col,
          isSorted,
          sortIndicator
        };
      });
  }

  get sortedResults() {
    const results = this.args.results || [];
    if (!results.length) return [];

    const field = this.sortField;
    const dir = this.sortDirection === 'asc' ? 1 : -1;

    return [...results].sort((a, b) => {
      let aVal, bVal;

      // Handle split columns
      if (field.startsWith('split_')) {
        const splitUuid = field.replace('split_', '');
        const valueKey = this.splitSortMode; // 'elapsed' or 'split'
        aVal = a.splitsMap?.[splitUuid]?.[valueKey] ?? Infinity;
        bVal = b.splitsMap?.[splitUuid]?.[valueKey] ?? Infinity;
      }
      // Handle finish column
      else if (field === 'finish') {
        if (this.splitSortMode === 'split') {
          aVal = a.finishSplit ?? Infinity;
          bVal = b.finishSplit ?? Infinity;
        } else {
          aVal = a.totalTimeInMilliseconds ?? Infinity;
          bVal = b.totalTimeInMilliseconds ?? Infinity;
        }
      }
      // Handle pos - use BullittTime Live sorting logic
      else if (field === 'pos') {
        // Sort using race position logic:
        // 1. Finished OK results first (by time)
        // 2. In-progress results (by furthest split)
        // 3. Not started (by bib)
        const aTime = a.totalTimeInMilliseconds;
        const bTime = b.totalTimeInMilliseconds;

        // If both have totalTimes and are OK, sort by them
        if (aTime != null && bTime != null && a.status === 'OK' && b.status === 'OK') {
          if (aTime !== bTime) {
            return (aTime > bTime ? 1 : -1) * dir;
          }
          // Use tie order if present
          if (a.tieOrder !== null && b.tieOrder !== null) {
            return (a.tieOrder - b.tieOrder) * dir;
          }
          return (a.uuid > b.uuid ? 1 : -1) * dir;
        }

        // If a has a totalTime and is OK, it is on top
        if (aTime != null && a.status === 'OK') {
          return -1 * dir;
        }
        // If b has a totalTime and is OK, it is on top
        if (bTime != null && b.status === 'OK') {
          return 1 * dir;
        }

        // Neither has a finish time - look at splits to determine who is furthest along
        const aSplits = a.splits || [];
        const bSplits = b.splits || [];

        let aNumSplits = 0;
        let bNumSplits = 0;
        let aLastSplitIndex = -1;
        let bLastSplitIndex = -1;

        aSplits.forEach((split, index) => {
          if (split.elapsed != null) {
            aNumSplits++;
            if (index > aLastSplitIndex) {
              aLastSplitIndex = index;
            }
          }
        });

        bSplits.forEach((split, index) => {
          if (split.elapsed != null) {
            bNumSplits++;
            if (index > bLastSplitIndex) {
              bLastSplitIndex = index;
            }
          }
        });

        // If neither has splits, sort by bib
        if (aNumSplits === 0 && bNumSplits === 0) {
          if (a.bib != null && b.bib != null) {
            return (a.bib > b.bib ? 1 : -1) * dir;
          }
          return (a.uuid > b.uuid ? 1 : -1) * dir;
        }

        // More splits = further along = higher in results
        if (aNumSplits !== bNumSplits) {
          return (aNumSplits > bNumSplits ? -1 : 1) * dir;
        }

        // Same number of splits - sort by elapsed time at last split
        const aLastElapsed = aSplits[aLastSplitIndex]?.elapsed;
        const bLastElapsed = bSplits[bLastSplitIndex]?.elapsed;

        if (aLastElapsed != null && bLastElapsed != null) {
          return (aLastElapsed > bLastElapsed ? 1 : aLastElapsed < bLastElapsed ? -1 : 0) * dir;
        }

        return 0;
      }
      // Handle other fields
      else {
        aVal = a[field];
        bVal = b[field];

        // Handle string comparison
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return aVal.localeCompare(bVal) * dir;
        }
      }

      // Numeric comparison
      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined || aVal === Infinity) return 1;
      if (bVal === null || bVal === undefined || bVal === Infinity) return -1;
      return (aVal - bVal) * dir;
    });
  }

  get columnGroups() {
    return this.preferences.getColumnGroups(this.args.splitColumns || []);
  }

  /**
   * Sticky columns (pos, bib, name) - always visible, sticky on mobile
   */
  get stickyColumns() {
    return this.visibleColumns.filter(col => col.sticky);
  }

  /**
   * Detail columns that are currently visible (club, class, country, etc.)
   * These appear in secondary row on mobile, normal columns on desktop
   */
  get visibleDetailColumns() {
    return this.visibleColumns.filter(col => col.isDetailColumn);
  }

  /**
   * Timing columns (splits and finish) - always in primary row
   */
  get timingColumns() {
    return this.visibleColumns.filter(col =>
      !col.sticky && !col.isDetailColumn
    );
  }

  /**
   * Whether any detail columns are visible
   */
  get hasVisibleDetailColumns() {
    return this.visibleDetailColumns.length > 0;
  }

  /**
   * Column count for mobile primary row (sticky + timing, used for colspan)
   */
  get mobileColumnCount() {
    return this.stickyColumns.length + this.timingColumns.length;
  }

  /**
   * Count of sticky columns (for secondary row colspan)
   */
  get stickyColumnCount() {
    return this.stickyColumns.length;
  }

  @action
  toggleSort(columnId) {
    const isSplitColumn = columnId.startsWith('split_') || columnId === 'finish';

    if (this.sortField === columnId) {
      // Same column clicked - cycle through options
      if (isSplitColumn) {
        // For splits: elapsed asc → elapsed desc → split asc → split desc → unsorted
        if (this.splitSortMode === 'elapsed' && this.sortDirection === 'asc') {
          this.sortDirection = 'desc';
        } else if (this.splitSortMode === 'elapsed' && this.sortDirection === 'desc') {
          this.splitSortMode = 'split';
          this.sortDirection = 'asc';
        } else if (this.splitSortMode === 'split' && this.sortDirection === 'asc') {
          this.sortDirection = 'desc';
        } else {
          // Reset to default sort
          this.sortField = 'pos';
          this.sortDirection = 'asc';
          this.splitSortMode = 'elapsed';
        }
      } else {
        // For non-splits: asc → desc → unsorted
        if (this.sortDirection === 'asc') {
          this.sortDirection = 'desc';
        } else {
          this.sortField = 'pos';
          this.sortDirection = 'asc';
        }
      }
    } else {
      // Different column - start fresh
      this.sortField = columnId;
      this.sortDirection = 'asc';
      this.splitSortMode = 'elapsed';
    }

    // Save to preferences
    this.preferences.updateSort(
      this.args.classGroupUuid,
      this.sortField,
      this.sortDirection,
      this.splitSortMode
    );
  }

  @action
  toggleColumnSelector() {
    this.showColumnSelector = !this.showColumnSelector;
  }

  @action
  toggleColumn(columnId) {
    // Pass current visible columns so toggleColumnVisibility preserves them
    this.preferences.toggleColumnVisibility(this.args.classGroupUuid, columnId, this.visibleColumnIds);
  }

  @action
  isColumnVisible(columnId) {
    return this.visibleColumnIds.includes(columnId);
  }

  /**
   * Toggle watchlist status for an entry
   */
  @action
  toggleWatchlist(entryUuid) {
    this.watchlist.toggleWatched(this.args.eventUuid, entryUuid);
  }
}
